import { Maatregel, RijdtGroepMaatregel } from "@/entities/vsm";
import { TreinserieDisplay } from "@/features/infra";
import { useMaatregelContext } from "@/features/vsm/maatregel-context";
import { useMaatregelVergelijking } from "@/features/vsm/vsm-vergelijking-context";
import { Icon, IconName } from "@/shared/components";
import { cn } from "@/shared/utils";
import { ReactNode } from "react";
import { MaatregelActions } from "./maatregel-actions";
import { MaatregelTags, VergelijkingTag } from "./maatregel-tags";
import { MaatregelToelichting } from "./maatregel-toelichting";

type MaatregelRowProps = {
    iconName: Extract<IconName, "train-conflict-1" | "train-conflict-2" | "train">;
    children?: ReactNode;
    content?: ReactNode;
    extraRegel?: ReactNode;
    maatregel: Maatregel;
};

export function MaatregelRow({ iconName, children, content, extraRegel, maatregel }: Readonly<MaatregelRowProps>) {
    return (
        <div
            className="border-subtle grid grid-cols-[auto_minmax(0,1fr)_auto] items-center border-b py-2"
            data-testid={`maatregel-${maatregel.treinserie}`}
        >
            <Icon name={iconName} className="mr-2" />
            {content ?? (
                <MaatregelContent maatregel={maatregel} extraRegel={extraRegel}>
                    {children}
                </MaatregelContent>
            )}
            <MaatregelActions maatregel={maatregel} />
        </div>
    );
}

type MaatregelContentProps = {
    maatregel: Maatregel;
    extraRegel?: ReactNode;
    children?: ReactNode;
};

function MaatregelContent({ maatregel, extraRegel, children }: Readonly<MaatregelContentProps>) {
    const { variant } = useMaatregelContext();
    const isBewerkbaar = variant === "nieuw";

    return (
        <div className="grid grid-cols-[56px_minmax(0,1fr)_auto] items-center gap-x-2">
            <span className="text-m leading-l min-h-6 w-14 font-medium">
                <TreinserieDisplay nummer={maatregel.treinserie} />
            </span>
            <div className="flex min-w-0 flex-wrap items-center gap-2">{children}</div>
            <MaatregelTags maatregel={maatregel} isBewerkbaar={isBewerkbaar} className={cn(extraRegel && "row-span-2")} />
            {extraRegel && <div className="col-span-2 col-start-1 mt-0.5">{extraRegel}</div>}
            {Maatregel.hasToelichtingOfSuggestie(maatregel) && (
                <div className="col-span-3 col-start-1 mt-0.5">
                    <MaatregelToelichting maatregel={maatregel} isBewerkbaar={isBewerkbaar} />
                </div>
            )}
        </div>
    );
}

type TreinserieLijstProps = {
    maatregel: RijdtGroepMaatregel;
};

export function TreinserieLijst({ maatregel }: Readonly<TreinserieLijstProps>) {
    const { variant } = useMaatregelContext();
    const { status } = useMaatregelVergelijking(maatregel);
    const isBewerkbaar = variant === "nieuw";
    const { maatregelen } = maatregel;

    return (
        <div className="flex min-h-6 items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-2">
                {maatregelen.map((maatregel, index) => (
                    <span key={maatregel.id} className="text-m leading-l flex items-center gap-1 font-medium">
                        <TreinserieDisplay nummer={maatregel.treinserie} />
                        <MaatregelTags maatregel={maatregel} isBewerkbaar={isBewerkbaar} className="gap-1" showVergelijkingTag={false} />
                        {index < maatregelen.length - 1 && ","}
                    </span>
                ))}
            </div>
            <VergelijkingTag status={status} />
        </div>
    );
}
